// Export-Helper fuer Transkripte: TXT und SRT.
// Reine Funktionen ohne Side-Effects, um sie leicht testbar zu halten.

import {
  speakerForSegment,
  groupConsecutiveBySpeaker,
} from './transcriptionResultUtils';

function pad(num, len = 2) {
  return String(num).padStart(len, '0');
}

function formatClockTime(seconds) {
  const safe = Math.max(0, Number(seconds) || 0);
  const m = Math.floor(safe / 60);
  const s = Math.floor(safe % 60);
  return `${pad(m)}:${pad(s)}`;
}

export function formatSrtTimestamp(seconds) {
  const safe = Math.max(0, Number(seconds) || 0);
  const totalMs = Math.round(safe * 1000);
  const ms = totalMs % 1000;
  const totalSec = Math.floor(totalMs / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return `${pad(h)}:${pad(m)}:${pad(s)},${pad(ms, 3)}`;
}

function segmentText(seg) {
  // seg.text bevorzugen, damit nutzerseitig bearbeiteter Text auch im Export
  // gewinnt. Der Backend-PATCH aktualisiert nur seg.text; seg.words bleibt
  // mit dem Whisper-Original stehen und wäre sonst stale.
  if (typeof seg.text === 'string' && seg.text.length > 0) {
    return seg.text;
  }
  if (seg.words && seg.words.length > 0) {
    return seg.words.map((w) => w.word).join(' ');
  }
  return '';
}

// Normalisiert Segmente zu Export-Einheiten {speaker, text, start, end}.
// Ohne Gruppierung ist eine Einheit ein Segment; mit Gruppierung ein Block
// aufeinanderfolgender Segmente desselben Sprechers — exakt die Logik der
// kompakten Online-Ansicht, deshalb wird groupConsecutiveBySpeaker
// wiederverwendet statt hier nachgebaut.
function toUnits(segments, groupBySpeaker) {
  const items = (segments || []).map((segment, originalIndex) => ({
    segment,
    originalIndex,
  }));

  if (!groupBySpeaker) {
    return items.map(({ segment, originalIndex }) => ({
      speaker: speakerForSegment(segment, originalIndex),
      text: segmentText(segment),
      start: segment.start,
      end: segment.end,
    }));
  }

  return groupConsecutiveBySpeaker(items).map((block) => ({
    speaker: block.speaker,
    text: block.items
      .map((item) => segmentText(item.segment))
      .filter(Boolean)
      .join(' ')
      .trim(),
    start: block.start,
    end: block.end,
  }));
}

export function buildTxt({
  segments,
  transcription,
  hasSegments,
  withSpeaker,
  groupBySpeaker,
}) {
  if (!hasSegments) {
    return transcription || '';
  }

  // Ohne erkannte Sprecher ist das Fallback-Label SPEAKER_<index> pro Segment
  // verschieden. Jede Zeile waere dann ein "Sprecherwechsel" und die Datei
  // komplett doppelzeilig — Leerzeilen deshalb nur bei echten Labels.
  const hasRealSpeakers = (segments || []).some((seg) => Boolean(seg.speaker));

  return toUnits(segments, groupBySpeaker)
    .map((unit, index, units) => {
      const prefix = withSpeaker ? `${unit.speaker}: ` : '';
      const time = `(${formatClockTime(unit.start)} - ${formatClockTime(unit.end)})`;
      const line = `${prefix}${unit.text} ${time}`;
      // Leerzeile beim Sprecherwechsel: entspricht den gaengigen
      // Transkriptionsleitfaeden und macht lange Protokolle lesbarer.
      const speakerChanged =
        hasRealSpeakers &&
        index > 0 &&
        units[index - 1].speaker !== unit.speaker;
      return speakerChanged ? `\n${line}` : line;
    })
    .join('\n');
}

// Bewusst ohne Sprecher-Gruppierung: ein Cue ueber mehrere Minuten mit einem
// ganzen Absatz Text ist als Untertitel unbrauchbar. Die Cue-Laenge ist bei
// SRT der eigentliche Zweck.
export function buildSrt({ segments, withSpeaker }) {
  const cues = [];
  let cueIndex = 1;
  toUnits(segments, false).forEach((unit) => {
    const start = Number(unit.start);
    const end = Number(unit.end);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) {
      return;
    }
    const text = unit.text.trim();
    if (!text) return;
    const prefix = withSpeaker ? `${unit.speaker}: ` : '';
    cues.push(
      [
        String(cueIndex),
        `${formatSrtTimestamp(start)} --> ${formatSrtTimestamp(end)}`,
        `${prefix}${text}`,
      ].join('\n'),
    );
    cueIndex += 1;
  });
  return cues.join('\n\n') + (cues.length > 0 ? '\n' : '');
}

export function buildExport({
  format,
  segments,
  transcription,
  hasSegments,
  withSpeaker,
  groupBySpeaker,
}) {
  if (format === 'srt') {
    return {
      content: buildSrt({ segments, withSpeaker }),
      extension: 'srt',
      mime: 'application/x-subrip',
    };
  }
  return {
    content: buildTxt({
      segments,
      transcription,
      hasSegments,
      withSpeaker,
      groupBySpeaker,
    }),
    extension: 'txt',
    mime: 'text/plain',
  };
}
